// ============================================================================
// CADERNO MARKDOWN VIEWER
// ARQUIVO: src/features/files/CadernoMarkdownViewer.jsx
// RESPONSABILIDADE: Fase B (leitor dentro do Portal) — reverte a Decisão "e"
//   de PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md ("nenhuma tela nova de
//   leitura de capítulo"), decisão revista ao vivo com o titular em
//   02/09/2026. Busca o CADERNO_<...>.md via getCadernoMarkdownText (função
//   dedicada — getSecureDocumentUrl não serve aqui, ver comentário na
//   function: devolve URL do Storage pra carregar via iframe/embed, e
//   fetch() dessa URL do lado do cliente esbarra em CORS) e renderiza com
//   react-markdown, nunca dangerouslySetInnerHTML — motivo: o Caderno cita
//   texto de WhatsApp/PDF de terceiro, fonte menos controlada que o
//   clausulado que ContractEngine/ProcurationEngine renderizam (esses sim
//   usam dangerouslySetInnerHTML, aceitável lá porque só o escritório
//   escreve o conteúdo).
//
// Renderizado como CONTEÚDO DE ABA (não modal) — pedido direto do titular
// 02/09/2026: "insere uma nova aba, é muito mais simples" — ver aba
// "Caderno de Trabalho" em CASE_TABS, entre "Caderno de Autos" e
// "Vínculos", CadernoCockpit.jsx.
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { functions } from "../../shared/api/firebase/config";

// Achado ao vivo (02/09/2026, titular): peças reais às vezes têm HTML
// embutido legítimo (ex.: `<div align="center">` pra centralizar o título
// da ação — convenção comum em petição). Sem rehype-raw, react-markdown
// não renderiza esse HTML, a tag aparece literal na tela. rehype-sanitize
// é par OBRIGATÓRIO, não opcional — sem ele, reabriria o vetor de XSS que
// a escolha de react-markdown (em vez de dangerouslySetInnerHTML) evitou.
// Schema estendido do padrão só pra permitir `align` (puramente
// apresentacional, não executa nada) em div/p — resto do allowlist
// padrão do rehype-sanitize (bem restritivo: sem script/on*/iframe).
// Exportados (não só usados aqui dentro) — Fase C4-bis (02/09/2026):
// PecaRedlineView.jsx reaproveita os dois pra renderizar o diff com a MESMA
// formatação rica desta tela, em vez de texto cru — pedido do titular ao
// ver o redline pela 1ª vez ("parece que está em formato markdown").
export const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    div: [...(defaultSchema.attributes?.div || []), "align"],
    p: [...(defaultSchema.attributes?.p || []), "align"],
  },
};

function extractDriveFileId(webViewLink) {
  const m = String(webViewLink || "").match(/\/file\/d\/([^/]+)/);
  return m ? m[1] : "";
}

// Achado ao vivo (16/09/2026, titular): copiar texto selecionado desta tela
// (Ctrl+C) e colar no Word carrega o estilo visual da casa (fundo dourado
// clarinho + borda lateral das citações de jurisprudência, cores dos
// títulos) como estilo inline — o Chrome serializa a seleção com os
// estilos computados dos elementos Tailwind. No Word isso aparece como uma
// "caixa" colorida em volta do texto. Colar como texto puro evita a caixa,
// mas também apaga negrito/itálico/títulos, que o titular precisa manter
// (cita jurisprudência e dispositivos legais, onde negrito/itálico
// carregam sentido). Correção: interceptar o evento `copy` e substituir o
// HTML da área de transferência por uma versão só com a ESTRUTURA
// semântica (negrito, itálico, títulos, listas, tabelas, links) sem
// nenhum atributo de estilo/cor/fundo — o Word aplica sua própria
// formatação padrão a essas tags, preto sobre branco, sem caixa.
const ATRIBUTOS_MANTIDOS_POR_TAG = {
  A: ["href"],
  TD: ["colspan", "rowspan"],
  TH: ["colspan", "rowspan"],
};

function limparElementoParaAreaDeTransferencia(elemento) {
  const manter = ATRIBUTOS_MANTIDOS_POR_TAG[elemento.tagName] || [];
  [...elemento.attributes].forEach((attr) => {
    if (!manter.includes(attr.name)) elemento.removeAttribute(attr.name);
  });
  [...elemento.children].forEach(limparElementoParaAreaDeTransferencia);
}

function limparHtmlParaAreaDeTransferencia(htmlOriginal) {
  const container = document.createElement("div");
  container.innerHTML = htmlOriginal;
  [...container.children].forEach(limparElementoParaAreaDeTransferencia);
  return container.innerHTML;
}

// Exportado (não só usado aqui dentro) — PecaRedlineView.jsx tem sua
// própria `<div>` rolável com o mesmo problema de copiar/colar, reaproveita
// este mesmo handler em vez de duplicar a lógica de limpeza.
export function handleCopyConteudoCaderno(event) {
  const selecao = window.getSelection();
  if (!selecao || selecao.rangeCount === 0 || selecao.isCollapsed) return;
  const fragmento = document.createElement("div");
  for (let i = 0; i < selecao.rangeCount; i += 1) {
    fragmento.appendChild(selecao.getRangeAt(i).cloneContents());
  }
  const htmlLimpo = limparHtmlParaAreaDeTransferencia(fragmento.innerHTML);
  event.clipboardData.setData("text/html", htmlLimpo);
  event.clipboardData.setData("text/plain", selecao.toString());
  event.preventDefault();
}

// Fase C1 (02/09/2026): a Seção "1. Panorama" repete dado que a aba Resumo
// já mostra — toda aba já está no contexto do processo selecionado, então
// repetir CNJ/partes/comarca aqui é redundante na TELA (progressive
// disclosure). Continua existindo no arquivo .md gerado (útil pra quem
// abre o arquivo bruto fora do Portal, sem esse contexto) — só não é
// renderizada aqui. Corta da "## 1. Panorama" até a próxima "## ".
function removerSecaoPanorama(md) {
  return String(md || "").replace(/##\s*1\.\s*Panorama[\s\S]*?(?=\n##\s*2\.)/, "");
}

// Cores da casa (CLAUDE.md): --brand-blue:#20243B · --highlight-gold:#C5A059.
export const markdownComponents = {
  h1: (props) => (
    <h1
      className="text-xl font-bold text-[#20243B] mt-6 mb-3 pb-2 border-b-2 border-[#C5A059] first:mt-0"
      {...props}
    />
  ),
  h2: (props) => (
    <h2 className="text-lg font-bold text-[#20243B] mt-6 mb-2" {...props} />
  ),
  h3: (props) => (
    <h3 className="text-[13px] font-bold text-[#C5A059] uppercase tracking-wide mt-4 mb-2" {...props} />
  ),
  h4: (props) => (
    <h4 className="text-sm font-bold text-gray-700 mt-3 mb-1.5" {...props} />
  ),
  blockquote: (props) => (
    <blockquote
      className="border-l-4 border-[#C5A059] bg-[#C5A059]/5 pl-4 py-2 my-3 text-gray-700 [&>p]:my-1"
      {...props}
    />
  ),
  strong: (props) => <strong className="font-bold text-[#20243B]" {...props} />,
  a: (props) => (
    <a
      className="text-[#C5A059] underline hover:text-[#a8844a] break-words"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  table: (props) => (
    <div className="overflow-x-auto my-3 rounded-lg border border-gray-200">
      <table className="min-w-full text-xs" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-slate-100" {...props} />,
  th: (props) => (
    <th className="border-b border-gray-200 px-2.5 py-2 text-left font-bold text-gray-600 uppercase text-[10px] tracking-wide" {...props} />
  ),
  td: (props) => <td className="border-b border-gray-100 px-2.5 py-2 align-top" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 my-2 space-y-1 text-sm" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 my-2 space-y-1 text-sm" {...props} />,
  li: (props) => <li className="text-gray-800" {...props} />,
  p: (props) => <p className="text-sm text-gray-800 leading-relaxed my-2" {...props} />,
  code: (props) => (
    <code className="bg-gray-100 text-[#20243B] px-1 py-0.5 rounded text-xs font-mono" {...props} />
  ),
  hr: (props) => <hr className="my-4 border-gray-200" {...props} />,
};

/**
 * Conteúdo da aba "Caderno de Trabalho". Não é modal — sem overlay, sem
 * onClose; monta e desmonta junto da troca de aba, como qualquer outra
 * aba do Cockpit.
 *
 * `onOpenNovaJanela` (opcional, 15/09/2026 — pedido do titular): quando
 * fornecido, renderiza um botão "Nova Janela" NA MESMA linha do "Abrir no
 * Drive" (mesmo header, mesmo `<div>` — não uma linha nova por baixo).
 * Cada chamador decide o próprio `window.open(...)` (rota/nome de janela
 * variam: "A Peça em Si" usa /peca/:workspaceId/:pieceId, "Caderno de
 * Trabalho" usa /caderno-trabalho/:workspaceId) — este componente só
 * expõe o slot visual, sem saber qual contexto o está usando.
 */
export function CadernoMarkdownViewer({ driveWebViewLink, onOpenNovaJanela }) {
  const [texto, setTexto] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  // Botão "Atualizar" (20/09/2026, pedido do titular: Normal e Redline
  // precisam ter EXATAMENTE os mesmos botões — o Redline já tinha). Mesma
  // técnica do PecaRedlineView: refreshTick refaz a busca e a posição de
  // rolagem é restaurada só no clique manual.
  const [refreshTick, setRefreshTick] = useState(0);
  const contentRef = useRef(null);
  const scrollPosRef = useRef(null);

  const handleAtualizar = () => {
    if (contentRef.current) scrollPosRef.current = contentRef.current.scrollTop;
    setRefreshTick((n) => n + 1);
  };

  useEffect(() => {
    if (!loading && scrollPosRef.current !== null && contentRef.current) {
      contentRef.current.scrollTop = scrollPosRef.current;
      scrollPosRef.current = null;
    }
  }, [loading]);

  useEffect(() => {
    const fileId = extractDriveFileId(driveWebViewLink);
    if (!fileId) {
      setErro("Caderno de Trabalho ainda não foi gerado para este caso.");
      setTexto("");
      return;
    }
    let cancelado = false;
    setLoading(true);
    setErro("");
    setTexto("");
    (async () => {
      try {
        const fn = httpsCallable(functions, "getCadernoMarkdownText");
        const payload = await fn({ driveFileId: fileId });
        const md = payload?.data?.text;
        if (typeof md !== "string") throw new Error("Resposta inesperada da função de leitura.");
        if (!cancelado) setTexto(removerSecaoPanorama(md));
      } catch (e) {
        console.error("[CadernoMarkdownViewer]", e);
        if (!cancelado) setErro(String(e?.message || e));
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [driveWebViewLink, refreshTick]);

  return (
    <div className="flex flex-col h-full">
      {driveWebViewLink ? (
        <div className="flex items-center justify-end gap-3 mb-2 shrink-0">
          {onOpenNovaJanela ? (
            <button
              type="button"
              onClick={onOpenNovaJanela}
              className="text-[10px] font-bold text-gray-400 hover:text-gray-600 uppercase tracking-wide whitespace-nowrap"
              title="Abrir esta tela numa janela separada"
            >
              <i className="fas fa-up-right-from-square mr-1"></i> Nova Janela
            </button>
          ) : null}
          <a
            href={driveWebViewLink}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] font-bold text-[#C5A059] hover:text-[#a8844a] uppercase tracking-wide whitespace-nowrap"
            title="Abrir no Drive (editar/baixar/comentar)"
          >
            <i className="fab fa-google-drive mr-1"></i> Abrir no Drive ↗
          </a>
          <button
            type="button"
            onClick={handleAtualizar}
            disabled={loading}
            className="text-[10px] font-bold text-[#C5A059] hover:text-[#a8844a] uppercase tracking-wide whitespace-nowrap disabled:opacity-40"
            title="Buscar de novo o texto atual no Drive"
          >
            <i className={`fas fa-rotate mr-1 ${loading ? "fa-spin" : ""}`}></i> Atualizar
          </button>
        </div>
      ) : null}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto custom-scrollbar border border-gray-200 rounded-lg px-6 py-4 bg-white"
        onCopy={handleCopyConteudoCaderno}
      >
        {loading ? (
          <div className="text-center text-xs text-gray-400 py-16">
            <i className="fas fa-spinner fa-spin mr-1.5"></i> Carregando Caderno de Trabalho...
          </div>
        ) : erro ? (
          <div className="text-center text-xs text-red-600 py-16">
            <i className="fas fa-triangle-exclamation mr-1.5"></i> {erro}
          </div>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            components={markdownComponents}
          >
            {texto}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
}
